import React from "react";
import { motion } from "framer-motion";
import { IMAGES } from "@/lib/assets";
import { TrendingDown, Building2, Users } from "lucide-react";

const CaseStudy = () => {
  return (
    <section id="case-study" data-testid="case-study-section" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={IMAGES.caseStudy} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19] via-[#0B0F19]/80 to-[#0B0F19]/50" />
      </div>

      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 grid lg:grid-cols-12 gap-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-8 bg-nex-gold" />
            <span className="mono-label">// Flagship Deploy · Case Study.01</span>
          </div>

          <h2 className="serif-head text-4xl md:text-6xl mb-6">
            The <span className="italic text-nex-gold">Exchange Alliance.</span>
            <br />
            First engine. Half a million saved.
          </h2>

          <p className="text-nex-chrome text-lg leading-relaxed mb-8 max-w-2xl">
            The Exchange Alliance is the first NEX Engine developed for The Life Foundation — a purpose-built operational surface that replaced fragmented county paperwork, disparate spreadsheets, and manual reporting with a single audited system.
          </p>

          <p className="text-nex-silver text-lg leading-relaxed mb-10 max-w-2xl">
            The result: <span className="serif-head text-nex-gold italic">over $500,000</span> in verified county-level savings, tighter compliance posture, and a data foundation that now powers strategic decisions at the board level.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { icon: TrendingDown, k: "$500K+", v: "verified county savings" },
              { icon: Building2, k: "1", v: "unified operational surface" },
              { icon: Users, k: "450+", v: "families empowered · LIFE" },
            ].map(({ icon: Icon, k, v }, i) => (
              <div key={i} data-testid={`case-stat-${i}`} className="card-industrial p-5">
                <Icon size={18} className="text-nex-gold mb-3" />
                <div className="serif-head text-2xl md:text-3xl text-nex-silver">{k}</div>
                <div className="mono-body text-[11px] mt-1">{v}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right — engineering readout */}
        <motion.aside
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
          className="lg:col-span-5"
        >
          <div className="card-industrial corner-tick p-7 h-full relative">
            <span className="mono-label">// Deployment.Manifest</span>
            <h3 className="serif-head text-2xl mt-3 mb-6">Exchange Alliance · v1</h3>

            <dl className="space-y-4 mono-body text-[12px]">
              {[
                ["client", "The Life Foundation"],
                ["parent", "LIFE LLC"],
                ["sector", "Public-sector / community"],
                ["scope", "County reporting + fund tracking"],
                ["deploy time", "14 days"],
                ["hosting", "Managed · monitored 24/7"],
                ["outcome", "Verified $500K+ savings"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-white/5 pb-3">
                  <dt className="text-nex-muted uppercase tracking-[0.25em] text-[10px]">{k}</dt>
                  <dd className="text-nex-silver text-right">{v}</dd>
                </div>
              ))}
            </dl>

            <div className="metal-divider my-6" />
            <p className="mono-body text-[11px] italic leading-relaxed">
              &gt; When the numbers can be audited by a county — the engine is real.
            </p>
          </div>
        </motion.aside>
      </div>
    </section>
  );
};

export default CaseStudy;
